import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { cn } from '@/utils';

interface CollapsibleSidebarProps {
  /** Label shown on the collapsed vertical strip and in the expanded header. */
  label: string;
  /** Controlled collapse state. */
  isCollapsed: boolean;
  /** Called when the user toggles collapse. */
  onCollapsedChange: (collapsed: boolean) => void;
  /** Content rendered in the expanded panel body. */
  children: React.ReactNode;
  /** Width of the expanded panel. Default: 360px. */
  expandedWidth?: string;
  /**
   * Drops the expanded panel's title bar (label + bottom border), leaving only a
   * borderless collapse button. Use when `children` already renders its own
   * header and the label would be redundant. The collapsed strip still shows the
   * label so the user knows what they are expanding.
   */
  hideHeader?: boolean;
  /**
   * Which edge the sidebar sits on. Mirrors the border, the chevrons, and the collapsed
   * strip's tooltip; the collapsed strip itself stays on the outer edge either way.
   * Default: 'right'.
   */
  side?: 'left' | 'right';
  /**
   * Render NO chrome row in the expanded panel — not even the bare collapse button
   * `hideHeader` leaves behind. For panels whose own first row is already a header (a title
   * bar, a pane switcher): that lone button row costs a whole row of vertical space for one
   * control. Such callers place {@link SidebarCollapseButton} in their header instead.
   *
   * The panel is then only collapsible through that button, so a `headerless` sidebar that
   * renders none is a one-way trip.
   */
  headerless?: boolean;
}

/**
 * The collapse control, for `headerless` sidebars to place in their own header row. Sized
 * to sit beside the small outline buttons those headers already carry, rather than matching
 * the chrome's own larger ghost button.
 */
export const SidebarCollapseButton: React.FC<{
  /** Sidebar label, for the tooltip. */
  label: string;
  side?: 'left' | 'right';
  onCollapse: () => void;
  className?: string;
}> = ({ label, side = 'right', onCollapse, className }) => {
  const Icon = side === 'left' ? PanelLeftClose : PanelRightOpen;
  return (
    <SimpleTooltipV2 content={`Collapse ${label}`} side="bottom">
      <Button
        variant="outline"
        size="sm"
        onClick={onCollapse}
        aria-label={`Collapse ${label}`}
        className={cn('h-5 w-5 shrink-0 p-0', className)}
      >
        <Icon className="w-3 h-3" />
      </Button>
    </SimpleTooltipV2>
  );
};

/**
 * Collapsible sidebar wrapper. Provides the consistent chrome (collapsed vertical strip +
 * expanded header with collapse button) used by NotesColumn, TimelineColumn, the Review
 * view's Output and reference columns, etc. The body of the expanded panel is rendered
 * from `children` so each consumer can manage its own data fetching, scroll container, and
 * entry rendering.
 *
 * Defaults to the right edge; pass `side="left"` for a left-hand column, which flips the
 * border to the other side and points the chevrons the other way.
 */
export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  label,
  isCollapsed,
  onCollapsedChange,
  children,
  expandedWidth = 'w-[360px]',
  hideHeader = false,
  side = 'right',
  headerless = false,
}) => {
  const width = isCollapsed ? 'w-10' : expandedWidth;
  const isLeft = side === 'left';
  // Chevrons follow the direction the panel travels, not the edge it lives on: collapsing a
  // left panel moves its content left, expanding it moves right (and the reverse on the
  // right edge).
  const ExpandIcon = isLeft ? PanelLeftOpen : PanelRightClose;
  const CollapseIcon = isLeft ? PanelLeftClose : PanelRightOpen;

  return (
    <div
      className={`shrink-0 ${isLeft ? 'border-r' : 'border-l'} flex transition-all duration-200 ${width}`}
    >
      {isCollapsed ? (
        <div className="flex flex-col items-center py-3 w-10 shrink-0">
          <SimpleTooltipV2 content={`Expand ${label}`} side={isLeft ? 'right' : 'left'}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCollapsedChange(false)}
              aria-label={`Expand ${label}`}
              className="h-6 w-6 p-0"
            >
              <ExpandIcon className="w-4 h-4" />
            </Button>
          </SimpleTooltipV2>
          <span className="text-xs text-muted-foreground mt-2 [writing-mode:vertical-lr] rotate-180">
            {label}
          </span>
        </div>
      ) : (
        // `min-h-0`: without it a flex child cannot shrink below its content height, so a
        // pane that scrolls internally (the Output field board) never gets a bounded
        // height and scrolls its whole self instead (BV-2129).
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {/* With a label the row splits label|button. With `hideHeader` the button is the
              only child, so the row's own alignment decides where it lands: on the INNER
              edge, facing the page content — the right end of a left sidebar, the left end
              of a right one. (`ml-auto` on the Button cannot do this: the flex child is the
              tooltip wrapper around it, not the Button itself.) */}
          {!headerless && (
            <div
              className={`flex items-center shrink-0 ${
                hideHeader ? (isLeft ? 'justify-end' : 'justify-start') : 'justify-between'
              } ${hideHeader ? 'px-2 pt-2' : 'px-4 py-3 border-b'}`}
            >
              {!hideHeader && <p className="text-sm font-semibold">{label}</p>}
              <SimpleTooltipV2 content={`Collapse ${label}`} side="bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCollapsedChange(true)}
                  aria-label={`Collapse ${label}`}
                  className="h-6 w-6 p-0"
                >
                  <CollapseIcon className="w-4 h-4" />
                </Button>
              </SimpleTooltipV2>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};
